import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { translationService, LanguageCode } from '../lib/i18n/translation-service';

interface I18nContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => Promise<void>;
  t: (text: string, options?: { fallback?: string }) => Promise<string>;
  tSync: (text: string, fallback?: string) => string;
  isLoading: boolean;
  supportedLanguages: { code: string; name: string }[];
  clearCache: () => void;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
  defaultLanguage?: LanguageCode;
  googleTranslateApiKey?: string;
}

export function I18nProvider({ 
  children, 
  defaultLanguage = 'en',
  googleTranslateApiKey 
}: I18nProviderProps) {
  const [language, setLanguageState] = useState<LanguageCode>(defaultLanguage);
  const [isLoading, setIsLoading] = useState(false);
  const [translationCache, setTranslationCache] = useState<Map<string, string>>(new Map());
  const languageRef = useRef<LanguageCode>(defaultLanguage);

  // Initialize translation service
  useEffect(() => {
    translationService.init(defaultLanguage, googleTranslateApiKey);
    // Clean cache on initialization to remove any warning messages (but keep valid translations)
    // This runs after init() which calls loadCache(), so it will clean warnings while preserving good translations
    const savedLang = localStorage.getItem('app_language') || defaultLanguage;
    languageRef.current = savedLang;
    setLanguageState(savedLang);
    translationService.setLanguage(savedLang);
    // Clean cache after a short delay to ensure it's loaded first
    setTimeout(() => {
      translationService.cleanCache();
    }, 100);
  }, [defaultLanguage, googleTranslateApiKey]);

  // Set language and translate all visible text
  const setLanguage = useCallback(async (lang: LanguageCode) => {
    setIsLoading(true);
    try {
      // Get current language from ref (always up-to-date)
      const previousLang = languageRef.current;
      
      // Update ref and state
      languageRef.current = lang;
      setLanguageState(lang);
      
      // Update translation service
      translationService.setLanguage(lang);
      
      // Clear component-level cache when language changes to force re-translation
      setTranslationCache(new Map());
      
      // If switching to a new language, clear cache entries for that language to ensure fresh translations
      if (lang !== previousLang && lang !== 'en') {
        translationService.clearLanguageCache(lang);
      }
      
      // Trigger re-translation of visible elements
      // Dispatch event to notify all components
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: lang } }));
      
      // Force a delay to ensure components have time to react
      const delay = lang === 'fr' ? 100 : 50;
      await new Promise(resolve => setTimeout(resolve, delay));
    } catch (error) {
      console.error('Error setting language:', error);
      // Don't throw - just log the error to prevent context loss
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array - use ref for previous language

  // Async translation function
  const t = useCallback(async (text: string, options?: { fallback?: string }): Promise<string> => {
    if (!text || !text.trim()) return text || options?.fallback || '';
    if (language === 'en') return text; // No translation needed for English
    
    // Check component-level cache first
    const cacheKey = `${text}:${language}`;
    const cached = translationCache.get(cacheKey);
    if (cached && !cached.includes('MYMEMORY WARNING')) {
      return cached;
    }
    
    try {
      const translated = await translationService.translate(text, language);
      // Only cache if it's a valid translation (not a warning or original text)
      if (translated && translated !== text && !translated.includes('MYMEMORY WARNING')) {
        setTranslationCache(prev => new Map(prev).set(cacheKey, translated));
      }
      return translated;
    } catch (error) {
      console.warn('Translation failed:', error);
      return options?.fallback || text;
    }
  }, [language, translationCache]);

  // Synchronous translation (returns cached or original text)
  const tSync = useCallback((text: string, fallback?: string): string => {
    if (!text || !text.trim()) return text || fallback || '';
    if (language === 'en') return text;
    
    const cacheKey = `${text}:${language}`;
    return translationCache.get(cacheKey) || text || fallback || '';
  }, [language, translationCache]);

  const clearCache = useCallback(() => {
    translationService.clearCache();
    setTranslationCache(new Map());
  }, []);

  const supportedLanguages = translationService.getSupportedLanguages();

  return (
    <I18nContext.Provider
      value={{
        language,
        setLanguage,
        t,
        tSync,
        isLoading,
        supportedLanguages,
        clearCache,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}

